/**
 * Materials R2 Helper Utilities
 * 
 * This module provides helper functions for Cloudflare R2 operations in the Materials Management System.
 * It implements the folder structure and operations defined in the CDN architecture.
 * 
 * Folder Structure:
 * avenue-materials/
 * ├── programs/{program_id}/sessions/{session_id}/versions/{version_id}/{uuid}-{filename}
 * ├── temp-uploads/{upload_session_id}/
 * └── archives/{date}/{reason}/...
 * 
 * Usage: These utilities are primarily used by Cloudflare Pages Functions
 * but can also be imported for client-side path generation and validation.
 */

/**
 * Generate R2 object key following the CDN architecture
 * @param {string} programId - Program identifier
 * @param {string} sessionId - Session identifier 
 * @param {string} versionId - Version identifier (e.g., "v2.1.0")
 * @param {string} fileUuid - UUID for the file
 * @param {string} originalFilename - Original filename
 * @returns {string} R2 object key
 */
export function createR2ObjectKey(programId, sessionId, versionId, fileUuid, originalFilename) {
  // Sanitize inputs
  const safeProgramId = sanitizePathComponent(programId);
  const safeSessionId = sanitizePathComponent(sessionId);
  const safeVersionId = sanitizePathComponent(versionId);
  const safeUuid = sanitizePathComponent(fileUuid);
  const safeFilename = sanitizeFilename(originalFilename);
  
  return `programs/${safeProgramId}/sessions/${safeSessionId}/versions/${safeVersionId}/${safeUuid}-${safeFilename}`;
}

/**
 * Generate temporary upload path
 * @param {string} uploadSessionId - Upload session identifier
 * @param {string} filename - Original filename
 * @returns {string} Temporary R2 object key
 */
export function createTempUploadPath(uploadSessionId, filename) {
  const safeSessionId = sanitizePathComponent(uploadSessionId);
  const safeFilename = sanitizeFilename(filename);
  return `temp-uploads/${safeSessionId}/${safeFilename}`;
}

/**
 * Generate archive path for deleted files
 * @param {string} originalPath - Original R2 object key
 * @param {string} reason - Archive reason (e.g., "admin_deleted", "expired")
 * @param {Date} archiveDate - Archive date (defaults to now)
 * @returns {string} Archive R2 object key
 */
export function createArchivePath(originalPath, reason = 'archived', archiveDate = new Date()) {
  const dateStr = archiveDate.toISOString().split('T')[0]; // YYYY-MM-DD
  const safeReason = sanitizePathComponent(reason);
  return `archives/${dateStr}/${safeReason}/${originalPath}`;
}

/**
 * Parse R2 object key to extract components
 * @param {string} objectKey - R2 object key
 * @returns {object} Parsed components or null if invalid
 */
export function parseR2ObjectKey(objectKey) {
  // Match programs path pattern
  const programMatch = objectKey.match(/^programs\/([^/]+)\/sessions\/([^/]+)\/versions\/([^/]+)\/([^/]+)-(.+)$/);
  if (programMatch) {
    return {
      type: 'program_material',
      programId: programMatch[1],
      sessionId: programMatch[2], 
      versionId: programMatch[3],
      fileUuid: programMatch[4],
      filename: programMatch[5]
    };
  }
  
  // Match temp upload pattern
  const tempMatch = objectKey.match(/^temp-uploads\/([^/]+)\/(.+)$/);
  if (tempMatch) {
    return {
      type: 'temp_upload',
      uploadSessionId: tempMatch[1],
      filename: tempMatch[2]
    };
  }
  
  // Match archive pattern
  const archiveMatch = objectKey.match(/^archives\/([^/]+)\/([^/]+)\/(.+)$/);
  if (archiveMatch) {
    return {
      type: 'archive',
      archiveDate: archiveMatch[1],
      reason: archiveMatch[2],
      originalPath: archiveMatch[3]
    };
  }
  
  return null;
}

/**
 * Generate CDN URL from R2 object key
 * @param {string} objectKey - R2 object key
 * @param {string} cdnBaseUrl - CDN base URL (e.g., "https://materials-cdn.avenuecareers.com")
 * @returns {string} CDN URL
 */
export function generateCdnUrl(objectKey, cdnBaseUrl = 'https://materials-cdn.avenuecareers.com') {
  // Remove trailing slash from base URL
  const baseUrl = cdnBaseUrl.replace(/\/$/, '');
  // Ensure object key doesn't start with slash
  const path = objectKey.startsWith('/') ? objectKey.substring(1) : objectKey;
  
  return `${baseUrl}/${path}`;
}

/**
 * Generate pre-signed URL for direct R2 access (if needed for uploads)
 * Note: This is a placeholder - actual implementation would require R2 credentials
 * @param {string} objectKey - R2 object key
 * @param {string} method - HTTP method ('GET', 'PUT', etc.)
 * @param {number} expirySeconds - URL expiry in seconds
 * @returns {Promise<string>} Pre-signed URL
 */
export async function generatePresignedUrl(objectKey, method = 'GET', expirySeconds = 3600) {
  // This would need to be implemented with actual R2 credentials
  // For now, return a placeholder that shows the expected structure
  console.warn('generatePresignedUrl not implemented - requires R2 credentials');
  return `https://placeholder-presigned-url.com/${objectKey}?method=${method}&expires=${expirySeconds}`;
}

/**
 * Sanitize path component to be URL-safe
 * @param {string} component - Path component to sanitize
 * @returns {string} Sanitized component
 */
function sanitizePathComponent(component) {
  if (!component) return '';
  
  return component
    .toString()
    .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace non-alphanumeric except dots and dashes
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^[._]/, '') // Remove leading dots or underscores
    .replace(/[._]$/, '') // Remove trailing dots or underscores
    .substring(0, 100); // Limit length
}

/**
 * Sanitize filename to be URL-safe while preserving extension
 * @param {string} filename - Original filename
 * @returns {string} Sanitized filename
 */
function sanitizeFilename(filename) {
  if (!filename) return 'untitled';
  
  // Split filename and extension
  const lastDotIndex = filename.lastIndexOf('.');
  let name = filename;
  let extension = '';
  
  if (lastDotIndex > 0 && lastDotIndex < filename.length - 1) {
    name = filename.substring(0, lastDotIndex);
    extension = filename.substring(lastDotIndex + 1);
  }
  
  // Sanitize name part
  const safeName = name
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^[._]/, '')
    .replace(/[._]$/, '')
    .substring(0, 200); // Leave room for extension
  
  // Sanitize extension
  const safeExtension = extension
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase()
    .substring(0, 10);
  
  return safeExtension ? `${safeName}.${safeExtension}` : safeName;
}

/**
 * Validate file type against allowed MIME types
 * @param {string} mimeType - File MIME type
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @returns {boolean} True if allowed
 */
export function validateFileType(mimeType, allowedTypes = null) {
  const defaultAllowedTypes = [
    'application/pdf',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'text/plain'
  ];
  
  const typesToCheck = allowedTypes || defaultAllowedTypes;
  return typesToCheck.includes(mimeType);
}

/**
 * Validate file size
 * @param {number} sizeBytes - File size in bytes
 * @param {number} maxSizeBytes - Maximum allowed size in bytes
 * @returns {boolean} True if within limit
 */
export function validateFileSize(sizeBytes, maxSizeBytes = 50 * 1024 * 1024) { // 50MB default
  return sizeBytes <= maxSizeBytes;
}

/**
 * Format file size for display
 * @param {number} sizeBytes - File size in bytes
 * @returns {string} Formatted size (e.g., "2.3 MB")
 */
export function formatFileSize(sizeBytes) {
  if (!sizeBytes || sizeBytes === 0) return '0 bytes';
  
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  let size = sizeBytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

/**
 * Generate file extension from MIME type
 * @param {string} mimeType - File MIME type
 * @returns {string} File extension
 */
export function getExtensionFromMimeType(mimeType) {
  const mimeToExtension = {
    'application/pdf': 'pdf',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'text/plain': 'txt'
  };
  
  return mimeToExtension[mimeType] || 'bin';
}

/**
 * Check if object key represents an active material (not temp or archived)
 * @param {string} objectKey - R2 object key
 * @returns {boolean} True if active material
 */
export function isActiveMaterial(objectKey) {
  const parsed = parseR2ObjectKey(objectKey);
  return parsed && parsed.type === 'program_material';
}

/**
 * Get version from object key
 * @param {string} objectKey - R2 object key
 * @returns {string|null} Version ID or null
 */
export function getVersionFromObjectKey(objectKey) {
  const parsed = parseR2ObjectKey(objectKey);
  return parsed && parsed.type === 'program_material' ? parsed.versionId : null;
}

/**
 * List all versions for a session (simulated - would need R2 API)
 * @param {string} programId - Program ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<string[]>} Array of version IDs
 */
export async function listSessionVersions(programId, sessionId) {
  // This would need actual R2 API implementation
  // For now, return a placeholder that shows expected structure
  console.warn('listSessionVersions not implemented - requires R2 API access');
  return ['v1.0.0', 'v1.1.0', 'v2.0.0'];
}

/**
 * Storage configuration constants
 */
export const STORAGE_CONFIG = {
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_MIME_TYPES: [
    'application/pdf',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'text/plain'
  ],
  BUCKET_FOLDERS: {
    PROGRAMS: 'programs',
    TEMP_UPLOADS: 'temp-uploads',
    ARCHIVES: 'archives'
  },
  CACHE_HEADERS: {
    IMMUTABLE: 'public, max-age=31536000, immutable', // 1 year
    NO_CACHE: 'no-cache, no-store, must-revalidate'
  }
};

/**
 * Default export with all utilities
 */
export default {
  createR2ObjectKey,
  createTempUploadPath,
  createArchivePath,
  parseR2ObjectKey,
  generateCdnUrl,
  generatePresignedUrl,
  validateFileType,
  validateFileSize,
  formatFileSize,
  getExtensionFromMimeType,
  isActiveMaterial,
  getVersionFromObjectKey,
  listSessionVersions,
  STORAGE_CONFIG
};